import { useEffect, useRef } from 'react';
import { readViewport } from '@/lib/use-viewport';

/**
 * use-swipe — EMSPCR Foundation v1.0 §7.2 swipe-gesture navigation.
 *
 * Attaches lightweight touchstart / touchmove / touchend handlers to
 * a scope element (defaults to document) and fires onLeft / onRight
 * on horizontal swipes that meet the §7.2 distance + angle + velocity
 * thresholds. No external gesture library needed — this is a thin
 * primitive tuned for ePCR tab navigation on phones.
 *
 * Phone-only by default (skipped on tablet / desktop) so the hook
 * doesn't hijack trackpad scrolling or precision-pointer drag on
 * larger viewports. Override via `allowOnDesktop` when a specific
 * surface (e.g. a carousel) wants swipe everywhere.
 *
 * The handler also respects native scrolling — if the user is
 * panning vertically, we bail so scroll isn't stolen.
 */

export interface SwipeOptions {
  /** Minimum horizontal distance in px (defaults to 60). */
  minDistance?: number;
  /** Max vertical drift before we bail to native scrolling (defaults to 40). */
  maxVerticalDrift?: number;
  /** Max duration in ms for the gesture to count (defaults to 500). */
  maxDuration?: number;
  /** Pass a ref to scope the listener; defaults to document. */
  scope?: React.RefObject<HTMLElement>;
  /** Fire swipes at tablet/desktop too (default false). */
  allowOnDesktop?: boolean;
  /** Skip when the swipe starts inside these selectors (inputs / editor regions). */
  ignoreInside?: string;
}

export function useSwipe(
  handlers: { onLeft?: () => void; onRight?: () => void },
  options: SwipeOptions = {},
): void {
  const {
    minDistance = 60,
    maxVerticalDrift = 40,
    maxDuration = 500,
    scope,
    allowOnDesktop = false,
    ignoreInside = 'input, textarea, select, button, [contenteditable="true"], [data-no-swipe]',
  } = options;

  const handlerRef = useRef(handlers);
  handlerRef.current = handlers;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!allowOnDesktop) {
      const vp = readViewport();
      if (!vp.isPhone) return;
    }

    const target: EventTarget = scope?.current ?? document;
    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let tracking = false;

    function onStart(e: Event) {
      const te = e as TouchEvent;
      if (te.touches.length !== 1) return;
      const t = te.touches[0];
      // Skip interactive elements
      const el = te.target as HTMLElement | null;
      if (el && ignoreInside && el.closest(ignoreInside)) return;
      startX = t.clientX;
      startY = t.clientY;
      startTime = performance.now();
      tracking = true;
    }

    function onEnd(e: Event) {
      if (!tracking) return;
      tracking = false;
      const te = e as TouchEvent;
      const t = te.changedTouches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      const dt = performance.now() - startTime;
      if (dt > maxDuration) return;
      if (Math.abs(dy) > maxVerticalDrift) return;
      if (Math.abs(dx) < minDistance) return;
      if (dx > 0) handlerRef.current.onRight?.();
      else         handlerRef.current.onLeft?.();
    }

    function onCancel() { tracking = false; }

    target.addEventListener('touchstart', onStart as EventListener, { passive: true });
    target.addEventListener('touchend',   onEnd   as EventListener, { passive: true });
    target.addEventListener('touchcancel', onCancel as EventListener);
    return () => {
      target.removeEventListener('touchstart', onStart as EventListener);
      target.removeEventListener('touchend',   onEnd   as EventListener);
      target.removeEventListener('touchcancel', onCancel as EventListener);
    };
  }, [minDistance, maxVerticalDrift, maxDuration, allowOnDesktop, scope, ignoreInside]);
}
